import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { ArrowRight, Lightbulb, Clock, TrendingUp, BookOpen } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const problemFrames = {
  tax: { icon: '⚠', pain: 'Missed deadline?', frame: 'Here\'s what it costs — and how to fix it' },
  compliance: { icon: '⚠', pain: 'Non-compliant?', frame: 'Real penalties we\'ve helped clients avoid' },
  finance: { icon: '📊', pain: 'Cash flow stress?', frame: 'Strategies that actually work for Indian SMEs' },
  fdi: { icon: '🌍', pain: 'Entering India?', frame: 'What foreign companies get wrong (and how we fix it)' },
  payroll: { icon: '💼', pain: 'Payroll mess?', frame: 'How we clean up payroll for 100+ companies' },
  business_tips: { icon: '💡', pain: 'Growth stalled?', frame: 'Practical advice from our CFO team' },
  quick_read: { icon: '⚡', pain: '2-min insight', frame: 'Quick wins you can implement today' },
};

export const InsightsShowcase = ({ onBookAppointment }) => {
  const [insights, setInsights] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchInsights = async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/blogs/?limit=6`);
        if (res.ok) {
          const data = await res.json();
          setInsights((data.blogs || []).slice(0, 3));
        }
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    fetchInsights();
  }, []);

  if (loading || insights.length === 0) return null;

  return (
    <section className="py-16 sm:py-20 bg-slate-950 relative overflow-hidden" data-testid="insights-showcase">
      {/* Subtle grid pattern */}
      <div className="absolute inset-0 opacity-[0.03]" style={{
        backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)',
        backgroundSize: '32px 32px'
      }} />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Header — NLP: Problem Agitation */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 bg-amber-500/10 text-amber-400 px-4 py-2 rounded-full text-sm font-medium mb-5">
            <Lightbulb className="w-4 h-4" />
            Problems We Solve Every Day
          </div>
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
            Real Challenges.<br />
            <span className="text-emerald-400">Practical Solutions.</span>
          </h2>
          <p className="text-base sm:text-lg text-gray-400 max-w-2xl mx-auto">
            These aren't theory — they're problems our clients faced and the exact strategies we used to solve them.
          </p>
        </div>

        {/* Insights Cards — NLP: Authority + Micro-commitment */}
        <div className="grid md:grid-cols-3 gap-6 mb-12">
          {insights.map((blog, idx) => {
            const frame = problemFrames[blog.category] || problemFrames.finance;
            const readTime = Math.max(2, Math.ceil((blog.content?.length || 500) / 1200));
            return (
              <div
                key={blog.blogId || blog.slug || `blog-${idx}`}
                onClick={() => navigate(`/blog/${blog.slug}`)}
                data-testid={`insight-card-${idx}`}
                className="group cursor-pointer bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 
                           hover:bg-white/10 hover:border-emerald-500/30 transition-all duration-300 hover:-translate-y-1"
              >
                {/* Problem Frame */}
                <div className="flex items-center gap-2 mb-4">
                  <span className="text-lg">{frame.icon}</span>
                  <span className="text-amber-400 text-sm font-semibold">{frame.pain}</span>
                </div>

                {/* Title */}
                <h3 className="text-lg font-bold text-white mb-3 leading-snug group-hover:text-emerald-300 transition-colors line-clamp-2">
                  {blog.title}
                </h3>

                {/* Excerpt — Solution Preview */}
                <p className="text-gray-400 text-sm leading-relaxed mb-4 line-clamp-3">
                  {blog.excerpt || frame.frame}
                </p>

                {/* Meta Row */}
                <div className="flex items-center justify-between pt-4 border-t border-white/10">
                  <div className="flex items-center gap-3">
                    <Badge className="bg-emerald-500/20 text-emerald-300 border-0 text-xs">
                      {(blog.category || 'finance').replace('_', ' ')}
                    </Badge>
                    <span className="text-gray-500 text-xs flex items-center gap-1">
                      <Clock className="w-3 h-3" /> {readTime} min read
                    </span>
                  </div>
                  <ArrowRight className="w-4 h-4 text-gray-500 group-hover:text-emerald-400 group-hover:translate-x-1 transition-all" />
                </div>
              </div>
            );
          })}
        </div>

        {/* CTA Row — NLP: Authority + Urgency */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
          <Button
            onClick={() => navigate('/blogs')}
            variant="outline"
            className="border-white/20 text-gray-300 hover:bg-white/10 hover:text-white px-6 rounded-full"
            data-testid="view-all-insights-btn"
          >
            <BookOpen className="w-4 h-4 mr-2" />
            View All Insights
          </Button>
          <Button
            onClick={() => onBookAppointment?.()}
            className="bg-emerald-600 hover:bg-emerald-700 text-white px-8 rounded-full shadow-lg shadow-emerald-900/30"
            data-testid="insights-book-consultation-btn"
          >
            Get Expert Advice Free
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>

        {/* Trust Signal */}
        <p className="text-center text-gray-600 text-xs mt-6">
          Based on real client engagements. Updated weekly by our editorial team.
        </p>
      </div>
    </section>
  );
};
